/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import {
  collection,
  getDocs,
  doc,
  writeBatch,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  FadeUpStagger,
  MotionCard,
  MotionButton,
} from "../motion/AdminMotion";

function buildStudentIdentity(payload = {}) {
  const firstName = String(payload.firstName || "").trim();
  const lastName = String(payload.lastName || "").trim();

  const combined =
    payload.name ||
    payload.fullName ||
    payload.displayName ||
    payload.studentName ||
    [firstName, lastName].filter(Boolean).join(" ").trim();

  const finalName = combined || "Unnamed Student";

  return {
    firstName,
    lastName,
    name: finalName,
    fullName: finalName,
    displayName: finalName,
    studentName: finalName,
  };
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function displayStudentName(student) {
  return (
    student?.name ||
    student?.fullName ||
    student?.displayName ||
    student?.studentName ||
    student?.childName ||
    [student?.firstName, student?.lastName].filter(Boolean).join(" ").trim() ||
    `Student ${String(student?.id || student?.studentId || "").slice(0, 6)}`
  );
}

function getLinkedParentUids(student) {
  return Array.isArray(student?.parentAuthUids) ? student.parentAuthUids : [];
}

function getLinkStatus(student) {
  return getLinkedParentUids(student).length > 0 ? "linked" : "unlinked";
}

function getLinkTone(status) {
  if (status === "linked") {
    return {
      color: "#027a48",
      background: "#dcfae6",
      border: "1px solid #abefc6",
    };
  }

  return {
    color: "#b42318",
    background: "#fee4e2",
    border: "1px solid #fecdca",
  };
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function makeStudentId() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `student_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  );
}

function parseCsvLine(line) {
  const result = [];
  let current = "";
  let insideQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const nextChar = line[i + 1];
    if (char === '"' && insideQuotes && nextChar === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === "," && !insideQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result.map((value) => value.replace(/^"|"$/g, "").trim());
}

function normalizeCsvHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/_/g, "");
}


function normaliseClassKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^yr\s*/g, "year")
    .replace(/^y(\d+)$/g, "year$1")
    .replace(/\s+/g, "");
}

function cleanClassLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const key = normaliseClassKey(raw);

  if (key === "nursery") return "Nursery";
  if (key === "reception") return "Reception";

  const yearMatch = key.match(/^year(\d+)$/);
  if (yearMatch) return `Year ${yearMatch[1]}`;

  return raw;
}

function makeClassIdFromLabel(label) {
  return cleanClassLabel(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export default function StudentsSection({ schoolId }) {
  const currentSchoolId = schoolId || "school_001";

  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [parents, setParents] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [linkFilter, setLinkFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [showStudentModal, setShowStudentModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);

  const [studentForm, setStudentForm] = useState({
    name: "",
    admissionNumber: "",
    classId: "",
    yearGroup: "",
  });

  const [linkingStudent, setLinkingStudent] = useState(null);
  const [parentSearch, setParentSearch] = useState("");
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState("");

  async function loadData() {
    try {
      setLoading(true);
      setError("");

      const [studentsSnap, classesSnap, parentsSnap] = await Promise.all([
        getDocs(collection(db, "schools", currentSchoolId, "students")),
        getDocs(collection(db, "schools", currentSchoolId, "classes")),
        getDocs(collection(db, "schools", currentSchoolId, "parents")),
      ]);

      const studentList = studentsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((student) => !student.deletedAt)
        .sort((a, b) =>
          displayStudentName(a).localeCompare(displayStudentName(b))
        );

      const classList = classesSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((item) => !item.deletedAt)
        .sort((a, b) =>
          String(a.name || a.className || a.yearGroup || "").localeCompare(
            String(b.name || b.className || b.yearGroup || "")
          )
        );

      const parentList = parentsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((item) => !item.deletedAt)
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        );

      setStudents(studentList);
      setClasses(classList);
      setParents(parentList);
    } catch (err) {
      console.error("Failed to load students:", err);
      setError("Failed to load students.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!currentSchoolId) return;
    loadData();
  }, [currentSchoolId]);

  const classMap = useMemo(() => {
    const map = {};
    classes.forEach((item) => {
      map[item.id] = item;
    });
    return map;
  }, [classes]);

  const parentMapByUid = useMemo(() => {
    const map = {};
    parents.forEach((parent) => {
      if (parent.parentUid) {
        map[parent.parentUid] = parent;
      }
    });
    return map;
  }, [parents]);

  function displayClassName(student) {
    const classDoc = student?.classId ? classMap[student.classId] : null;

    return (
      classDoc?.name ||
      classDoc?.className ||
      classDoc?.yearGroup ||
      student?.className ||
      student?.yearGroup ||
      "Unassigned"
    );
  }

  function displayAdmission(student) {
    return student?.admissionNumber || student?.admissionNo || "—";
  }

  const summary = useMemo(() => {
    const linked = students.filter((s) => getLinkStatus(s) === "linked").length;
    const unlinked = students.filter((s) => getLinkStatus(s) === "unlinked").length;
    const assigned = students.filter(
      (s) => displayClassName(s) !== "Unassigned"
    ).length;

    return {
      total: students.length,
      linked,
      unlinked,
      assigned,
    };
  }, [students, classMap]);

  const classOptions = useMemo(() => {
    return classes.map((item) => ({
      id: item.id,
      label: item.name || item.className || item.yearGroup || item.id,
    }));
  }, [classes]);

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();

    let rows = [...students];

    if (linkFilter !== "all") {
      rows = rows.filter((student) => getLinkStatus(student) === linkFilter);
    }

    if (classFilter !== "all") {
      rows = rows.filter((student) => {
        const classDoc = student.classId ? classMap[student.classId] : null;
        return student.classId === classFilter || classDoc?.id === classFilter;
      });
    }

    if (!q) return rows;

    return rows.filter((student) => {
      const classDoc = student.classId ? classMap[student.classId] : null;

      const hay = [
        student.name,
        student.fullName,
        student.displayName,
        student.admissionNumber,
        student.admissionNo,
        student.yearGroup,
        student.className,
        classDoc?.name,
        classDoc?.className,
        classDoc?.yearGroup,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [students, search, linkFilter, classFilter, classMap]);

  const classInsights = useMemo(() => {
    const map = {};

    students.forEach((student) => {
      const className = displayClassName(student);

      if (!map[className]) {
        map[className] = {
          name: className,
          total: 0,
          linked: 0,
          unlinked: 0,
        };
      }

      map[className].total += 1;

      if (getLinkStatus(student) === "linked") {
        map[className].linked += 1;
      } else {
        map[className].unlinked += 1;
      }
    });

    return Object.values(map).sort((a, b) => {
      if (a.name === "Unassigned") return 1;
      if (b.name === "Unassigned") return -1;
      return a.name.localeCompare(b.name);
    });
  }, [students, classMap]);

  const filteredParents = useMemo(() => {
    const q = parentSearch.trim().toLowerCase();

    let rows = [...parents];

    if (!q) return rows;

    return rows.filter((parent) => {
      const hay = [parent.name, parent.email, parent.parentUid]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [parents, parentSearch]);

  function resetStudentForm() {
    setStudentForm({
      name: "",
      admissionNumber: "",
      classId: "",
      yearGroup: "",
    });
    setEditingStudent(null);
  }

  function openNewStudentModal() {
    resetStudentForm();
    setShowStudentModal(true);
  }

  function openEditStudentModal(student) {
    setEditingStudent(student);
    setStudentForm({
      name: student.name || student.fullName || student.displayName || "",
      admissionNumber: student.admissionNumber || student.admissionNo || "",
      classId: student.classId || "",
      yearGroup: student.yearGroup || "",
    });
    setShowStudentModal(true);
  }

  function closeStudentModal() {
    setShowStudentModal(false);
    resetStudentForm();
  }

  async function handleSaveStudent(e) {
    e.preventDefault();

    const cleanName = String(studentForm.name || "").trim();
    const cleanAdmission = String(studentForm.admissionNumber || "").trim();

    if (!cleanName) {
      setError("Student name is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const selectedClass = studentForm.classId
        ? classMap[studentForm.classId]
        : null;

const identity = buildStudentIdentity({
  name: cleanName,
});

const payload = {
  ...identity,
  admissionNumber: cleanAdmission,
  admissionNo: cleanAdmission,
  classId: studentForm.classId || "",
  className: selectedClass?.name || selectedClass?.className || "",
  yearGroup: selectedClass?.yearGroup || studentForm.yearGroup || "",
  updatedAt: serverTimestamp(),
};
      if (editingStudent?.id) {
        await setDoc(
          doc(db, "schools", currentSchoolId, "students", editingStudent.id),
          payload,
          { merge: true }
        );
        setSuccess("Student updated successfully.");
      } else {
        const studentId = makeStudentId();

        await setDoc(
          doc(db, "schools", currentSchoolId, "students", studentId),
          {
            studentId,
            schoolId: currentSchoolId,
            ...payload,
            parentAuthUids: [],
            parentIds: [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }
        );

        setSuccess("Student created successfully.");
      }

      closeStudentModal();
      await loadData();
    } catch (err) {
      console.error("Failed to save student:", err);
      setError(err?.message || "Failed to save student.");
    } finally {
      setSaving(false);
    }
  }

  function openLinkParentModal(student) {
    setLinkingStudent(student);
    setParentSearch("");
    setSelectedParentIds(getLinkedParentUids(student));
  }

  function closeLinkParentModal() {
    setLinkingStudent(null);
    setParentSearch("");
    setSelectedParentIds([]);
  }

  function toggleParentSelection(parentUid) {
    setSelectedParentIds((prev) =>
      prev.includes(parentUid)
        ? prev.filter((id) => id !== parentUid)
        : [...prev, parentUid]
    );
  }


  function resolveClassFromCsv(value) {
    const rawClass = String(value || "").trim();
    const classLabel = cleanClassLabel(rawClass);
    const classKey = normaliseClassKey(classLabel);

    if (!classLabel) {
      return {
        classId: "",
        yearGroup: "",
        rawClass,
        classKey: "",
        classStatus: "missing",
        classLabel: "",
      };
    }

    const match = classes.find((c) => {
      const values = [c.id, c.name, c.className, c.yearGroup, c.label]
        .filter(Boolean)
        .map((x) => normaliseClassKey(x));

      return values.includes(classKey);
    });

    return {
      classId: match?.id || "",
      yearGroup:
        match?.yearGroup || match?.name || match?.className || classLabel,
      rawClass,
      classKey,
      classStatus: match ? "matched" : "new",
      classLabel,
    };
  }

  async function handleCsvSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setCsvBusy(true);
      setCsvError("");
      setCsvRows([]);
      setCsvFileName(file.name || "");

      const text = await file.text();
      const lines = text
.split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

      if (lines.length < 2) {
        throw new Error("CSV must include a header row and at least one student.");
      }

      const headers = parseCsvLine(lines[0]).map(normalizeCsvHeader);
      const requiredHeaders = ["name", "admissionnumber", "classname", "yeargroup"];

      const validHeaders =
        headers.length === requiredHeaders.length &&
        requiredHeaders.every((header) => headers.includes(header));

      if (!validHeaders) {
        throw new Error(
          "Invalid student CSV format. Required headers: Name, AdmissionNumber, ClassName, YearGroup"
        );
      }

      const existingAdmissionNumbers = new Set(
        students
          .map((student) =>
            String(student.admissionNumber || student.admissionNo || "")
              .trim()
              .toLowerCase()
          )
          .filter(Boolean)
      );

      const seenAdmissionNumbers = new Set();

      const rows = lines.slice(1).map((line, index) => {
        const cells = parseCsvLine(line);
        const row = {};

        headers.forEach((header, i) => {
          row[header] = cells[i] || "";
        });

        const name = String(row.name || "").trim();
        const admissionNumber = String(row.admissionnumber || "").trim();
        const classText = String(row.classname || "").trim();
        const yearGroup = String(row.yeargroup || "").trim();
        const classMatch = resolveClassFromCsv(classText);
        const admissionKey = admissionNumber.toLowerCase();
        const errors = [];

        if (!name) {
          errors.push("Missing name");
        }

        if (!admissionNumber) {
          errors.push("Missing admission number");
        }

        if (!classText) {
          errors.push("Missing class name");
        }

        if (!yearGroup) {
          errors.push("Missing year group");
        }

        if (classText && classMatch.classStatus !== "matched") {
          errors.push("Class does not exist");
        }

        if (admissionKey && seenAdmissionNumbers.has(admissionKey)) {
          errors.push("Duplicate admission number in CSV");
        }

        if (admissionKey && existingAdmissionNumbers.has(admissionKey)) {
          errors.push("Admission number already exists");
        }

        if (admissionKey) {
          seenAdmissionNumbers.add(admissionKey);
        }

        return {
          rowNumber: index + 2,
          name,
          admissionNumber,
          classId: classMatch.classId,
          yearGroup,
          rawClass: classMatch.rawClass,
          classKey: classMatch.classKey,
          classStatus: classMatch.classStatus,
          classLabel: classMatch.classLabel,
          errors,
        };
      });

      if (!rows.length) {
        throw new Error("No student rows found in CSV.");
      }

      const hasErrors = rows.some((row) => row.errors.length > 0);

      setCsvRows(rows);

      if (hasErrors) {
        setCsvError("CSV contains errors. Fix the highlighted rows before importing.");
      }
    } catch (err) {
      console.error("CSV read failed:", err);
      setCsvRows([]);
      setCsvError(err?.message || "Failed to read CSV file.");
    } finally {
      setCsvBusy(false);
      e.target.value = "";
    }
  }

  async function handleImportCsvStudents() {
    if (!csvRows.length || !currentSchoolId) return;

    const rowsWithErrors = csvRows.filter((row) => row.errors?.length);

    if (rowsWithErrors.length) {
      setCsvError("CSV still contains errors. Fix the file and upload it again before importing.");
      return;
    }

    try {
      setCsvBusy(true);
      setCsvError("");
      setError("");
      setSuccess("");

      const batch = writeBatch(db);

      csvRows.forEach((row) => {
        const studentId = makeStudentId();
        const resolvedClass = row.classId ? classMap[row.classId] : null;
        const resolvedClassName =
          resolvedClass?.name ||
          resolvedClass?.className ||
          row.classLabel ||
          row.rawClass ||
          "";

        batch.set(doc(db, "schools", currentSchoolId, "students", studentId), {
          studentId,
          schoolId: currentSchoolId,
          name: row.name,
          fullName: row.name,
          displayName: row.name,
          studentName: row.name,
          admissionNumber: row.admissionNumber || "",
          admissionNo: row.admissionNumber || "",
          classId: row.classId || "",
          className: resolvedClassName,
          yearGroup: row.yearGroup || resolvedClass?.yearGroup || resolvedClassName || "",
          parentAuthUids: [],
          parentIds: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });

      batch.set(
        doc(db, "schools", currentSchoolId, "settings", "school"),
        {
          studentsReady: true,
          onboardingStep: 4,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await batch.commit();

      setSuccess(`${csvRows.length} students imported successfully.`);
      setCsvRows([]);
      setCsvFileName("");
      await loadData();
    } catch (err) {
      console.error("CSV import failed:", err);
      setCsvError(err?.message || "Failed to import students.");
    } finally {
      setCsvBusy(false);
    }
  }

async function handleSaveParentLinks() {
  if (!linkingStudent?.id) return;

  try {
    setSaving(true);
    setError("");
    setSuccess("");

    const nextLinkedParentUids = [...selectedParentIds];

    const nextParentDocIds = nextLinkedParentUids
      .map((uid) => parentMapByUid[uid]?.id)
      .filter(Boolean);

    const syncLinks = httpsCallable(functions, "setParentStudentLinks");

    await syncLinks({
      schoolId: currentSchoolId,
      studentId: linkingStudent.id,
      parentUids: nextLinkedParentUids,
      parentDocIds: nextParentDocIds,
    });

    setSuccess("Parent links updated successfully.");
    closeLinkParentModal();
    await loadData();
  } catch (err) {
    console.error("Failed to update parent links:", err);
    setError(err?.message || "Failed to update parent links.");
  } finally {
    setSaving(false);
  }
}

  return (
    <div style={styles.page}>
      <MotionCard style={styles.hero} hover>
        <div style={styles.heroLeft}>
          <div style={styles.heroEyebrow}>PresentPlus Admin</div>
          <h2 style={styles.heroTitle}>Students</h2>
          <p style={styles.heroText}>
            Manage student visibility, class grouping, creation, editing, and
            parent-linking readiness from one clean operational view.
          </p>
        </div>

        <div style={styles.heroBadge}>
          <div style={styles.heroBadgeLabel}>Students in system</div>
          <div style={styles.heroBadgeValue}>{formatCount(summary.total)}</div>
        </div>
      </MotionCard>

      {error ? <div style={styles.errorBox}>{error}</div> : null}
      {success ? <div style={styles.successBox}>{success}</div> : null}

      <section style={styles.statGrid}>
        <FadeUpStagger>
          <StatCard label="Total Students" value={summary.total} tone="blue" />
          <StatCard
            label="Linked to Parents"
            value={summary.linked}
            tone="success"
          />
          <StatCard
            label="Unlinked"
            value={summary.unlinked}
            tone="danger"
          />
          <StatCard
            label="Class Assigned"
            value={summary.assigned}
            tone="warning"
          />
        </FadeUpStagger>
      </section>



      <MotionCard style={styles.panel} hover>
        <div style={styles.panelHeadRow}>
          <div>
            <div style={styles.panelEyebrow}>Bulk upload</div>
            <h3 style={styles.panelTitle}>Import students by CSV</h3>
            <div style={styles.panelText}>
              Upload a CSV with exact headers: Name, AdmissionNumber, ClassName,
              YearGroup. Classes must already exist before importing students.
            </div>
          </div>

          <label style={styles.primaryButton}>
            Choose CSV
            <input type="file" accept=".csv,text/csv" onChange={handleCsvSelected} style={{ display: "none" }} />
          </label>
        </div>

        {csvError ? <div style={styles.errorBox}>{csvError}</div> : null}

        <div style={styles.csvInfoBox}>
          <div>
            <div style={styles.csvTitle}>{csvFileName || "No CSV selected yet"}</div>
            <div style={styles.csvText}>Required headers: Name, AdmissionNumber, ClassName, YearGroup.</div>
          </div>

          <MotionButton
            type="button"
            onClick={handleImportCsvStudents}
            disabled={!csvRows.length || csvBusy || csvRows.some((row) => row.errors?.length)}
            style={{
              ...styles.primaryButton,
              ...(!csvRows.length || csvBusy || csvRows.some((row) => row.errors?.length)
                ? styles.disabledButton
                : {}),
            }}
          >
            {csvBusy ? "Working..." : `Import ${csvRows.length || 0} students`}
          </MotionButton>
        </div>

        {csvRows.length ? (
          <div style={styles.csvPreview}>
            {csvRows.slice(0, 5).map((row) => (
              <div
                key={`${row.rowNumber}-${row.name || "student"}`}
                style={{
                  ...styles.csvPreviewRow,
                  ...(row.errors?.length ? styles.csvPreviewRowError : {}),
                }}
              >
                <strong>{row.name || `Row ${row.rowNumber}`}</strong>
                <span>{row.admissionNumber || "No admission number"}</span>
                <span>
                  {row.errors?.length
                    ? row.errors.join(", ")
                    : `Matched: ${row.yearGroup}`}
                </span>
              </div>
            ))}
            {csvRows.length > 5 ? <div style={styles.csvMore}>+{csvRows.length - 5} more students ready to import</div> : null}
          </div>
        ) : null}
      </MotionCard>

      <MotionCard style={styles.panel}>
        <div style={styles.panelHeadRow}>
          <div>
            <div style={styles.panelEyebrow}>Directory controls</div>
            <h3 style={styles.panelTitle}>Student records</h3>
            <div style={styles.panelText}>
              Search by student details, filter by parent-linking state, narrow
              by class, and add new students from here.
            </div>
          </div>

          <MotionButton onClick={openNewStudentModal} style={styles.primaryButton}>
            Add New Student
          </MotionButton>
        </div>

        <div style={styles.controlsRow}>
          <div style={styles.searchWrap}>
            <input
              style={styles.searchInput}
              placeholder="Search by name, admission number, year, or class"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={styles.filterWrap}>
            {["all", "linked", "unlinked"].map((item) => {
              const active = linkFilter === item;
              return (
                <MotionButton
                  key={item}
                  onClick={() => setLinkFilter(item)}
                  style={{
                    ...styles.filterChip,
                    ...(active ? styles.filterChipActive : {}),
                  }}
                >
                  {item.charAt(0).toUpperCase() + item.slice(1)}
                </MotionButton>
              );
            })}
          </div>

          <select
            style={styles.select}
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
          >
            <option value="all">All Classes</option>
            {classOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </MotionCard>

      <section style={styles.mainGrid}>
        <MotionCard style={styles.panel}>
          <div style={styles.panelHead}>
            <div>
              <div style={styles.panelEyebrow}>Student directory</div>
              <h3 style={styles.panelTitle}>Operational list</h3>
            </div>
          </div>

          {loading ? (
            <div style={styles.emptyState}>Loading students...</div>
          ) : filteredStudents.length ? (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Student</th>
                    <th style={styles.th}>Class / Year</th>
                    <th style={styles.th}>Admission</th>
                    <th style={styles.th}>Parent Linking</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => {
                    const status = getLinkStatus(student);
                    const tone = getLinkTone(status);
                    const parentCount = getLinkedParentUids(student).length;

                    return (
                      <tr key={student.id}>
                        <td style={styles.td}>
                          <div style={styles.studentCell}>
                            <div style={styles.studentAvatar}>
                              {displayStudentName(student).slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <div style={styles.studentName}>
                                {displayStudentName(student)}
                              </div>
                              <div style={styles.studentSub}>ID: {student.id}</div>
                            </div>
                          </div>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.classText}>
                            {displayClassName(student)}
                          </div>
                        </td>

                        <td style={styles.td}>{displayAdmission(student)}</td>

                        <td style={styles.td}>
                          <div style={styles.linkCell}>
                            <span style={{ ...styles.statusBadge, ...tone }}>
                              {status === "linked" ? "Linked" : "Unlinked"}
                            </span>

                            <div style={styles.linkSub}>
                              {status === "linked"
                                ? `${parentCount} parent account${parentCount > 1 ? "s" : ""}`
                                : "Needs parent linking"}
                            </div>
                          </div>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.actionsWrap}>
                            <MotionButton
                              onClick={() => openEditStudentModal(student)}
                              style={styles.secondaryButton}
                            >
                              Edit
                            </MotionButton>

                            <MotionButton
                              onClick={() => openLinkParentModal(student)}
                              style={
                                status === "linked"
                                  ? styles.manageLinkBtn
                                  : styles.linkParentBtn
                              }
                            >
                              {status === "linked" ? "Manage Parent" : "Link Parent"}
                            </MotionButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={styles.emptyState}>No students match this view yet.</div>
          )}
        </MotionCard>

        <MotionCard style={styles.sidePanel}>
          <div style={styles.panelHead}>
            <div>
              <div style={styles.panelEyebrow}>Insights</div>
              <h3 style={styles.panelTitle}>Class grouping</h3>
              <div style={styles.panelText}>
                Quick view of where parent linking is complete and where it still
                needs work.
              </div>
            </div>
          </div>

          <div style={styles.insightList}>
            {classInsights.length ? (
              classInsights.map((group) => (
                <div key={group.name} style={styles.insightCard}>
                  <div style={styles.insightTop}>
                    <div style={styles.insightTitle}>{group.name}</div>
                    <div style={styles.insightTotal}>{formatCount(group.total)}</div>
                  </div>

                  <div style={styles.insightStats}>
                    <div style={styles.insightGood}>
                      Linked: {formatCount(group.linked)}
                    </div>
                    <div style={styles.insightBad}>
                      Unlinked: {formatCount(group.unlinked)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={styles.emptyState}>No class insight available yet.</div>
            )}
          </div>
        </MotionCard>
      </section>

      {showStudentModal ? (
        <div style={styles.modalOverlay} onClick={closeStudentModal}>
          <MotionCard style={styles.modalCard} hover={false}>
            <div onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div>
                  <div style={styles.panelEyebrow}>Student management</div>
                  <h3 style={styles.modalTitle}>
                    {editingStudent ? "Edit student" : "Add new student"}
                  </h3>
                  <div style={styles.panelText}>
                    Create a student record or update their class assignment.
                  </div>
                </div>

                <MotionButton onClick={closeStudentModal} style={styles.modalCloseBtn}>
                   <span style={{ transform: "translateY(-1px)" }}>×</span>
                </MotionButton>
              </div>

              <form onSubmit={handleSaveStudent} style={styles.formGrid}>
                <input
                  style={styles.input}
                  placeholder="Student name"
                  value={studentForm.name}
                  onChange={(e) =>
                    setStudentForm((p) => ({ ...p, name: e.target.value }))
                  }
                />

                <input
                  style={styles.input}
                  placeholder="Admission number"
                  value={studentForm.admissionNumber}
                  onChange={(e) =>
                    setStudentForm((p) => ({
                      ...p,
                      admissionNumber: e.target.value,
                    }))
                  }
                />

                <select
                  style={styles.select}
                  value={studentForm.classId}
                  onChange={(e) =>
                    setStudentForm((p) => ({ ...p, classId: e.target.value }))
                  }
                >
                  <option value="">Select class</option>
                  {classOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <div style={styles.modalActions}>
                  <MotionButton
                    onClick={closeStudentModal}
                    style={styles.secondaryButton}
                    type="button"
                    disabled={saving}
                  >
                    Cancel
                  </MotionButton>

                  <MotionButton
                    style={styles.primaryButton}
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : editingStudent ? "Save Changes" : "Create Student"}
                  </MotionButton>
                </div>
              </form>
            </div>
          </MotionCard>
        </div>
      ) : null}

      {linkingStudent ? (
        <div style={styles.modalOverlay} onClick={closeLinkParentModal}>
          <MotionCard style={styles.modalCard} hover={false}>
            <div onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div>
                  <div style={styles.panelEyebrow}>Parent linking</div>
                  <h3 style={styles.modalTitle}>Link parent</h3>
                  <div style={styles.panelText}>
                    Link one or more parents to {displayStudentName(linkingStudent)}.
                  </div>
                </div>

                <MotionButton onClick={closeLinkParentModal} style={styles.modalCloseBtn}>
                   <span style={{ transform: "translateY(-1px)" }}>×</span>
                </MotionButton>
              </div>

              <input
                style={styles.searchInput}
                placeholder="Search parent by name or email"
                value={parentSearch}
                onChange={(e) => setParentSearch(e.target.value)}
              />

              <div style={styles.modalList}>
                {filteredParents.length ? (
                  <FadeUpStagger delay={0.04} gap={0.03}>
                    {filteredParents.map((parent) => {
                      const uid = parent.parentUid || "";
                      const selected = uid && selectedParentIds.includes(uid);

                      return (
                        <MotionButton
                          key={parent.id}
                          onClick={() => uid && toggleParentSelection(uid)}
                          style={{
                            ...styles.modalStudentRow,
                            ...(selected ? styles.modalStudentRowActive : {}),
                            ...(uid ? {} : styles.modalStudentRowDisabled),
                          }}
                          disabled={!uid}
                        >
                          <div style={styles.modalStudentMain}>
                            {parent.name || "Unnamed Parent"}
                          </div>
                          <div
                            style={{
                              ...styles.modalStudentSub,
                              ...(selected
                                ? { color: "rgba(255,255,255,0.88)" }
                                : {}),
                            }}
                          >
                            {normalizeEmail(parent.email || "—")}
                            {!uid ? " · Invite not yet accepted" : ""}
                          </div>
                        </MotionButton>
                      );
                    })}
                  </FadeUpStagger>
                ) : (
                  <div style={styles.emptyState}>No parents found.</div>
                )}
              </div>

              <div style={styles.modalActions}>
                <MotionButton
                  onClick={closeLinkParentModal}
                  style={styles.secondaryButton}
                  type="button"
                  disabled={saving}
                >
                  Cancel
                </MotionButton>

                <MotionButton
                  onClick={handleSaveParentLinks}
                  style={styles.primaryButton}
                  type="button"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Parent Links"}
                </MotionButton>
              </div>
            </div>
          </MotionCard>
        </div>
      ) : null}
    </div>
  );
}

function StatCard({ label, value, tone = "default" }) {
  const toneMap = {
    default: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,251,255,0.98) 100%)",
      border: "1px solid rgba(219,231,255,0.95)",
      boxShadow: "0 20px 40px rgba(79, 99, 255, 0.08)",
      valueColor: "#112042",
    },
    success: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(238,252,246,0.98) 100%)",
      border: "1px solid rgba(171,239,198,0.9)",
      boxShadow: "0 20px 40px rgba(2,122,72,0.08)",
      valueColor: "#027a48",
    },
    warning: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,247,236,0.98) 100%)",
      border: "1px solid rgba(254,223,137,0.88)",
      boxShadow: "0 20px 40px rgba(181,71,8,0.08)",
      valueColor: "#b54708",
    },
    danger: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,245,248,0.98) 100%)",
      border: "1px solid rgba(254,205,202,0.88)",
      boxShadow: "0 20px 40px rgba(180,35,24,0.08)",
      valueColor: "#b42318",
    },
    blue: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(239,245,255,0.98) 100%)",
      border: "1px solid rgba(191,220,255,0.9)",
      boxShadow: "0 20px 40px rgba(23,92,211,0.08)",
      valueColor: "#175cd3",
    },
  };

  const toneStyle = toneMap[tone] || toneMap.default;

  return (
    <MotionCard
      style={{
        ...styles.statCard,
        ...toneStyle,
      }}
    >
      <div style={styles.statLabel}>{label}</div>
      <div
        style={{
          ...styles.statValue,
          color: toneStyle.valueColor,
        }}
      >
        {formatCount(value)}
      </div>
    </MotionCard>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 22,
  },

  hero: {
    borderRadius: 28,
    padding: "30px 28px",
    background: "var(--pp-brand-gradient)",
    color: "#ffffff",
    boxShadow: "0 28px 70px rgba(79, 99, 255, 0.18)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
  },

  heroLeft: {
    minWidth: 0,
  },

  heroEyebrow: {
    fontSize: 12,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    fontWeight: 800,
    opacity: 0.86,
    marginBottom: 10,
  },



  disabledButton: { opacity: 0.55, cursor: "not-allowed" },

  csvInfoBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    padding: 18,
    borderRadius: 22,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    marginTop: 12,
  },

  csvTitle: { color: "#112042", fontWeight: 950, marginBottom: 4 },

  csvText: { color: "#64748b", fontSize: 14, lineHeight: 1.5 },

  csvPreview: { display: "grid", gap: 10, marginTop: 14 },

  csvPreviewRow: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr 1fr",
    gap: 12,
    padding: 14,
    borderRadius: 18,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    color: "#334155",
    fontSize: 14,
  },

  csvPreviewRowError: {
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#991b1b",
  },

  csvMore: { color: "#64748b", fontWeight: 900, padding: "4px 2px" },

  heroTitle: {
    margin: 0,
    fontSize: 42,
    lineHeight: 1.04,
    letterSpacing: "-0.03em",
    fontWeight: 900,
  },

  heroText: {
    margin: "12px 0 0",
    fontSize: 16,
    lineHeight: 1.65,
    opacity: 0.95,
    maxWidth: 760,
  },

  heroBadge: {
    minWidth: 220,
    borderRadius: 24,
    padding: "18px 20px",
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.18)",
    backdropFilter: "blur(12px)",
    boxShadow: "0 16px 36px rgba(18, 28, 80, 0.16)",
  },

  heroBadgeLabel: {
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.82,
    marginBottom: 8,
    fontWeight: 700,
  },

  heroBadgeValue: {
    fontSize: 34,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: "-0.03em",
  },

  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 16,
  },

  statCard: {
    borderRadius: 24,
    padding: "22px 22px",
    background: "rgba(255,255,255,0.86)",
    border: "1px solid rgba(255,255,255,0.72)",
    boxShadow: "0 18px 40px rgba(79, 99, 255, 0.08)",
    backdropFilter: "blur(10px)",
  },

  statLabel: {
    fontSize: 12,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontWeight: 800,
    color: "#5d6b8a",
    marginBottom: 12,
  },

  statValue: {
    fontSize: 42,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: "-0.04em",
  },

  panel: {
    background: "#ffffff",
    borderRadius: 28,
    border: "1px solid #e9eefb",
    boxShadow: "0 18px 42px rgba(17, 32, 66, 0.06)",
    padding: 24,
  },

  sidePanel: {
    background: "#ffffff",
    borderRadius: 28,
    border: "1px solid #e9eefb",
    boxShadow: "0 18px 42px rgba(17, 32, 66, 0.06)",
    padding: 24,
    minWidth: 0,
  },

  panelHead: {
    marginBottom: 18,
  },

  panelHeadRow: {
    marginBottom: 18,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
  },

  panelEyebrow: {
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#6b7aa0",
    fontWeight: 800,
    marginBottom: 8,
  },

  panelTitle: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.1,
    letterSpacing: "-0.03em",
    fontWeight: 900,
    color: "#112042",
  },

  panelText: {
    marginTop: 8,
    color: "#667085",
    fontSize: 15,
    lineHeight: 1.6,
    maxWidth: 760,
  },

  primaryButton: {
    padding: "14px 18px",
    borderRadius: 16,
    border: "none",
    background: "var(--pp-brand-gradient)",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 16px 30px rgba(79, 99, 255, 0.18)",
    fontSize: 15,
    whiteSpace: "nowrap",
  },

  secondaryButton: {
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid #d8e1f2",
    background: "#fff",
    color: "#334155",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 14,
  },

  linkParentBtn: {
    padding: "12px 14px",
    borderRadius: 14,
    border: "none",
    background: "var(--pp-brand-gradient)",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 14,
  },

  manageLinkBtn: {
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid #cfe0ff",
    background: "#eef4ff",
    color: "#1d4ed8",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 14,
  },

  controlsRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto auto",
    gap: 14,
    alignItems: "center",
  },

  searchWrap: {
    minWidth: 0,
  },

  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "14px 16px",
    borderRadius: 18,
    border: "1px solid #d8e1f2",
    background: "#fbfdff",
    fontSize: 15,
    color: "#14213d",
    outline: "none",
  },

  filterWrap: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },

  filterChip: {
    padding: "10px 14px",
    borderRadius: 999,
    border: "1px solid #d8e1f2",
    background: "#ffffff",
    color: "#475467",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },

  filterChipActive: {
    background: "var(--pp-brand-gradient)",
    border: "1px solid transparent",
    color: "#ffffff",
    boxShadow: "0 12px 24px rgba(79, 99, 255, 0.18)",
  },

  select: {
    minWidth: 170,
    boxSizing: "border-box",
    padding: "14px 16px",
    borderRadius: 18,
    border: "1px solid #d8e1f2",
    background: "#fbfdff",
    fontSize: 15,
    color: "#14213d",
    outline: "none",
  },

  mainGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.5fr) minmax(320px, 0.9fr)",
    gap: 18,
    alignItems: "start",
  },

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },

  th: {
    textAlign: "left",
    padding: "0 0 14px",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#667085",
    borderBottom: "1px solid #e9eefb",
  },

  td: {
    padding: "18px 0",
    borderBottom: "1px solid #edf2fb",
    verticalAlign: "middle",
  },

  studentCell: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    minWidth: 240,
  },

  studentAvatar: {
    width: 42,
    height: 42,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #eef4ff 0%, #f4f1ff 100%)",
    color: "#2743dd",
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: "0.04em",
    flexShrink: 0,
  },

  studentName: {
    fontSize: 15,
    fontWeight: 800,
    color: "#112042",
    lineHeight: 1.3,
  },

  studentSub: {
    marginTop: 4,
    fontSize: 12,
    color: "#7b8ba7",
    lineHeight: 1.3,
  },

  classText: {
    fontSize: 14,
    fontWeight: 700,
    color: "#334155",
  },

  linkCell: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 30,
    padding: "0 12px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    width: "fit-content",
  },

  linkSub: {
    fontSize: 12,
    color: "#667085",
    lineHeight: 1.4,
  },

  actionsWrap: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-start",
  },

  insightList: {
    display: "grid",
    gap: 12,
  },

  insightCard: {
    borderRadius: 18,
    border: "1px solid #e7edf8",
    background: "#fbfdff",
    padding: "16px 16px",
  },

  insightTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },

  insightTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: "#112042",
  },

  insightTotal: {
    fontSize: 14,
    fontWeight: 900,
    color: "#4f63ff",
  },

  insightStats: {
    marginTop: 10,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontSize: 13,
  },

  insightGood: {
    color: "#027a48",
    fontWeight: 700,
  },

  insightBad: {
    color: "#b42318",
    fontWeight: 700,
  },

  emptyState: {
    padding: "18px 0 4px",
    color: "#667085",
    fontSize: 15,
    lineHeight: 1.6,
  },

  errorBox: {
    borderRadius: 18,
    border: "1px solid #fecdca",
    background: "#fff1f0",
    color: "#b42318",
    padding: "14px 16px",
    fontWeight: 700,
  },

  successBox: {
    borderRadius: 18,
    border: "1px solid #abefc6",
    background: "#ecfdf3",
    color: "#027a48",
    padding: "14px 16px",
    fontWeight: 700,
  },

modalOverlay: {
  position: "fixed",
  inset: 0,
  zIndex: 1000,
  background: "rgba(15, 23, 42, 0.42)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
},

modalCard: {
  width: "100%",
  maxWidth: 860,
  maxHeight: "92vh",
  overflow: "auto",
  borderRadius: 28,
  background: "rgba(255,255,255,0.78)",
  border: "1px solid rgba(255,255,255,0.75)",
  boxShadow: "0 30px 90px rgba(15,23,42,0.22)",
  padding: 28,
},


  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 18,
  },

  modalTitle: {
    margin: 0,
    fontSize: 32,
    lineHeight: 1.06,
    letterSpacing: "-0.03em",
    fontWeight: 900,
    color: "#112042",
  },

modalCloseBtn: {
  width: 46,
  height: 46,
  borderRadius: 16,
  border: "1px solid rgba(255,255,255,0.7)",
  background: "#ffffff",
  color: "#0f172a",
  fontSize: 24,
  fontWeight: 900,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
  padding: 0,
  flexShrink: 0,
},

  formGrid: {
    display: "grid",
    gap: 14,
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "14px 16px",
    borderRadius: 18,
    border: "1px solid #d8e1f2",
    background: "#fbfdff",
    fontSize: 15,
    color: "#14213d",
    outline: "none",
  },

  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 6,
    flexWrap: "wrap",
  },

  modalList: {
    display: "grid",
    gap: 10,
    marginTop: 16,
    maxHeight: 340,
    overflowY: "auto",
    paddingRight: 2,
  },

  modalStudentRow: {
    borderRadius: 18,
    border: "1px solid #e6ecf7",
    background: "#fbfdff",
    padding: "14px 16px",
    textAlign: "left",
    cursor: "pointer",
  },

  modalStudentRowActive: {
    background: "var(--pp-brand-gradient)",
    border: "1px solid transparent",
    color: "#ffffff",
    boxShadow: "0 16px 28px rgba(79, 99, 255, 0.2)",
  },

  modalStudentRowDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },

  modalStudentMain: {
    fontSize: 15,
    fontWeight: 800,
    lineHeight: 1.35,
  },

  modalStudentSub: {
    marginTop: 6,
    fontSize: 13,
    color: "#667085",
    lineHeight: 1.45,
  },
};